/** @jsx jsx */

import React, { useEffect, useState, useMemo } from "react";

import { jsx } from "@emotion/core";
import { Modal, message, Typography } from "antd";
import { Formik, Form } from "formik";

import {
  modalStyles,
  acceptRejectFoldRadioStyle
} from "./AcceptRejectFoldStyles";
import Button from "../../../components/button";

import { EligibilityService } from "../../../services/eligibilityService";
import { EligibilityDetailsState } from "../../../store/eligibility/state";
import {
  acceptRejectFoldConstants as CONSTANTS,
  acceptRejectFoldValidationSchema as validationSchema
} from "./AcceptRejectFoldConstants";

import useAPI from "../../../helperHooks/useAPI";
import { FormRadioButton, FormInput } from "../../../components/formHelpers";
import useRedirect from "../../../helperHooks/useRedirect";
import useLoanStatus from "../../../hooks/auth/useLoanStatus";

const { Text } = Typography;

interface Props {
  eligibilityDetails: EligibilityDetailsState;
}

export interface AcceptRejectFoldInternalState {
  reason: string | null;
  manualReason: string | null;
  isRejectReasonManual: boolean | null;
}

/**
 *  Accept / Reject flow
 */
const AcceptRejectFold: React.FC<Props> = (props: Props) => {
  const {
    eligibilityDetails: { reason, isRejectReasonManual, manualReason }
  } = props;

  const redirect = useRedirect();

  const { callAPI: getRejectionReasons, ...rejectionReasonsData } = useAPI<
    void,
    string[]
  >(EligibilityService.rejectionReasons);

  const { callAPI: updateLoanStatus, ...updateLoanStatusData } = useAPI(
    EligibilityService.updateLoanStatus
  );

  const { updateStatus: updateGlobalStatus } = useLoanStatus();

  //to control modal visibility
  const [open, setOpen] = useState(false);

  //fetching reject reasons
  useEffect(() => {
    getRejectionReasons();
  }, [getRejectionReasons]);

  const disabled = updateLoanStatusData.loading;

  const initialValues: AcceptRejectFoldInternalState = {
    isRejectReasonManual,
    manualReason,
    reason
  };

  const acceptLoanAmount = () => {
    updateLoanStatus(
      {
        isAccepted: true
      },
      response => {
        if (response) {
          updateGlobalStatus({
            status: response.status,
            subStatus: response.subStatus
          });
        }
        message.success(CONSTANTS.labels.acceptMessage);
        redirect(routes => routes.eligibility.bankAccountDetails);
      }
    );
  };

  const handleCancel = () => setOpen(false);

  const onRejectClick = () => {
    setOpen(true);
  };

  const onReject = async (values: AcceptRejectFoldInternalState) => {
    const isManual = values.reason === CONSTANTS.labels.other;
    const rejectReason = isManual ? values.manualReason : values.reason;
    if (rejectReason) {
      updateLoanStatus(
        {
          isAccepted: false,
          rejectReason,
          isRejectReasonManual: isManual
        },
        response => {
          if (response) {
            updateGlobalStatus({
              status: response.status,
              subStatus: response.subStatus
            });
          }
          message.success(CONSTANTS.labels.rejectMessage);
          setOpen(false);
        }
      );
    }
  };

  const rejectReasons = useMemo(() => {
    const reasons = {};
    (rejectionReasonsData.response || []).forEach(
      item => (reasons[item] = item)
    );
    return reasons;
  }, [rejectionReasonsData.response]);

  const renderModal = (
    <React.Fragment>
      {rejectionReasonsData.response?.length && (
        <Formik
          initialValues={initialValues}
          onSubmit={onReject}
          validateOnChange
          validateOnBlur
          validationSchema={validationSchema}
        >
          {({ values, handleSubmit }) => (
            <Form>
              <Modal
                css={modalStyles}
                visible={open}
                onCancel={
                  !updateLoanStatusData.loading ? handleCancel : undefined
                }
                confirmLoading={updateLoanStatusData.loading}
                title={CONSTANTS.labels.rejectModalHeader}
                closable={false}
                footer={
                  <section className="d-flex justify-content-center mb-3">
                    <Button
                      className="mr-3"
                      onClick={
                        !updateLoanStatusData.loading ? handleCancel : undefined
                      }
                    >
                      {CONSTANTS.buttons.cancel}
                    </Button>
                    <Button
                      type="primary"
                      disabled={!values.reason}
                      onClick={() => handleSubmit()}
                    >
                      {CONSTANTS.buttons.submit}
                    </Button>
                  </section>
                }
              >
                <Text className="reject-modal-label">
                  {CONSTANTS.labels.rejectReasonTitle}
                </Text>
                <FormRadioButton
                  name="reason"
                  className="d-flex py-3"
                  options={rejectReasons}
                  customStyles={acceptRejectFoldRadioStyle}
                />
                {values.reason === CONSTANTS.labels.other && (
                  <FormInput
                    className="pl-4 ml-3"
                    name="manualReason"
                    placeholder={CONSTANTS.placeholders.manualReason}
                    autoFocus
                  />
                )}
              </Modal>
            </Form>
          )}
        </Formik>
      )}
    </React.Fragment>
  );

  return (
    <div className="d-flex justify-content-end my-4">
      <Button
        disabled={disabled}
        onClick={onRejectClick}
        containerDivClassName="pr-3"
      >
        {CONSTANTS.buttons.reject}
      </Button>
      <Button
        type="primary"
        onClick={acceptLoanAmount}
        disabled={disabled}
        loading={updateLoanStatusData.loading}
      >
        {CONSTANTS.buttons.accept}
      </Button>
      {renderModal}
    </div>
  );
};

export default AcceptRejectFold;
